import React, { forwardRef, useState } from "react";
import { IoIosArrowBack, IoIosArrowForward } from "react-icons/io";
import i18n from "../../lib/i18n";

const OrderDetailsPop = forwardRef(({ data, onClose }, ref) => {
  // Safe combination of main image and extra photos array
  const allPhotos = [data?.ad_main_img, ...(data?.photos || [])].filter(Boolean);
  
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const t = i18n.t.bind(i18n);

  const nextSlide = () => {
    const nextIndex = (currentIndex + 1) % allPhotos.length;
    setLoading(true);
    setCurrentIndex(nextIndex);
  };

  const prevSlide = () => {
    const prevIndex = currentIndex === 0 ? allPhotos.length - 1 : currentIndex - 1;
    setLoading(true);
    setCurrentIndex(prevIndex);
  };

  const handleImageLoad = () => {
    setLoading(false);
  };

  const hasMultiplePhotos = allPhotos.length > 1;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div 
        className="bg-thirdly rounded-2xl shadow-lg lg:w-[55%] md:w-[75%] sm:w-[90%] w-full max-w-4xl p-6 relative overflow-y-auto max-h-[90vh] scrollable text-white" 
        ref={ref}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-3 right-4 text-gray-400 hover:text-red-500 text-xl transition-colors"
        >
          ✕
        </button>

        {/* Title & Basic Identifiers */}
        <div className="text-center mb-4">
          <h2 className="text-2xl font-bold mb-1">
            {data?.ad_title || t("order.no_title")}
          </h2>
          <p className="text-sm text-gray-400">
            {t("order.ad_id")}: #{data?.id} | {t("order.position")}: {data?.position}
          </p>
        </div>

        {/* Image Carousel Slider */}
        <div className="relative flex justify-center items-center w-full h-64 sm:h-80 bg-black/20 rounded-xl overflow-hidden mb-6">
          {loading && (
            <div className="absolute z-10 bg-black/60 text-white px-4 py-2 rounded">
              Loading...
            </div>
          )}
          {allPhotos.length > 0 ? (
            <img
              key={currentIndex}
              src={allPhotos[currentIndex]}
              alt="Ad Visual"
              onLoad={handleImageLoad}
              className="w-full h-full object-contain"
            />
          ) : (
            <div className="text-gray-400">{t("order.no_images")}</div>
          )}
          
          {hasMultiplePhotos && (
            <>
              <button
                onClick={prevSlide}
                className="absolute top-1/2 -translate-y-1/2 left-2 bg-secondary p-1.5 rounded-full text-white hover:bg-opacity-80 transition"
              >
                <IoIosArrowBack size={20} />
              </button>

              <button
                onClick={nextSlide}
                className="absolute top-1/2 -translate-y-1/2 right-2 bg-secondary p-1.5 rounded-full text-white hover:bg-opacity-80 transition"
              >
                <IoIosArrowForward size={20} />
              </button>
            </>
          )}
        </div>

        {/* Dynamic Details Layout Grid */}
        <div className="space-y-6">
          
          {/* Section 1: Ad Core Properties & Status */}
          <div className="border-b border-gray-700 pb-4">
            <h3 className="text-lg font-semibold text-secondary mb-3">
              {t("order.ads_information")}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              <p><span className="text-gray-400">{t("order.description")}:</span> {data?.ad_description || "—"}</p>
              <p><span className="text-gray-400">{t("order.type")}:</span> {data?.ad_type} <span className="text-xs text-gray-500">({data?.ad_type_id})</span></p>
              <p><span className="text-gray-400">{t("order.ad_status")}:</span> {data?.ad_status} <span className="text-xs text-gray-500">({data?.ad_status_id})</span></p>
              <p><span className="text-gray-400">{t("order.shape")}:</span> {data?.ad_shape}</p>
              <p>
                <span className="text-gray-400">{t("order.top_result")}:</span>{" "}
                <span className={`px-1.5 py-0.5 text-xs rounded font-bold ${data?.topResult ? "bg-amber-500 text-black" : "bg-gray-600"}`}>
                  {data?.topResult ? t("order.yes") : t("order.no")}
                </span>
              </p>
              <p><span className="text-gray-400">{t("order.max_photos")}:</span> {data?.max_number_of_photo}</p>
            </div>
          </div>

          {/* Section 2: Placement & Classification */}
          <div className="border-b border-gray-700 pb-4">
            <h3 className="text-lg font-semibold text-secondary mb-3">
              {t("order.classification_location")}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              <p><span className="text-gray-400">{t("order.category")}:</span> {data?.ad_category_name} <span className="text-xs text-gray-500">({data?.ad_category_id})</span></p>
              <p><span className="text-gray-400">{t("order.sub_category")}:</span> {data?.ad_sub_category_name} <span className="text-xs text-gray-500">({data?.ad_sub_category_id})</span></p>
              <p><span className="text-gray-400">{t("order.governorate")}:</span> {data?.ad_governorate_name} <span className="text-xs text-gray-500">({data?.ad_governorate_id})</span></p>
              <p><span className="text-gray-400">{t("order.location")}:</span> {data?.ad_location || "—"}</p>
            </div>
            {/* Custom inputs handler if they contain metadata dynamically */}
            {data?.ad_inputs && data.ad_inputs.length > 0 && (
              <div className="mt-4 p-4 bg-black/20 rounded-xl border border-gray-700/40 w-full">
                <span className="text-xs font-bold text-gray-400 uppercase tracking-wider block mb-3">
                  {t("order.additional_inputs")}
                </span>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                  {data.ad_inputs.map((item, index) => {
                    // Fallback checks to extract the label field cleanly
                    const displayLabel = item?.label || item?.name || item?.key || `Field ${index + 1}`;
                    
                    // Handle boolean values cleanly, otherwise render standard strings/numbers
                    let displayValue = "—";
                    if (typeof item?.value === "boolean") {
                      displayValue = item.value ? t("order.yes") : t("order.no");
                    } else if (item?.value !== undefined && item?.value !== null) {
                      displayValue = String(item.value);
                    }

                    return (
                      <div 
                        key={index} 
                        className="flex justify-between items-center p-2.5 bg-thirdly/40 rounded-lg border border-gray-800 hover:border-gray-700/60 transition-colors"
                      >
                        <span className="text-gray-400 font-medium">{displayLabel}</span>
                        <span 
                          className="font-semibold text-white max-w-[60%] truncate" 
                          title={displayValue}
                        >
                          {displayValue}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Section 3: Pricing, Timeline & Scheduling */}
          <div className="border-b border-gray-700 pb-4">
            <h3 className="text-lg font-semibold text-secondary mb-3">
              {t("order.pricing_timing")}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              <p><span className="text-gray-400">{t("order.ad_price")}:</span> {data?.ad_price} {data?.ad_currency}</p>
              <p><span className="text-gray-400">{t("order.duration")}:</span> {data?.duration} {t("order.days")}</p>
              <p><span className="text-gray-400">{t("order.scheduled_at")}:</span> {data?.ad_schedule_at || "—"}</p>
              <p><span className="text-gray-400">{t("order.start_time")}:</span> {data?.start_time || "—"}</p>
              <p><span className="text-gray-400">{t("order.end_time")}:</span> {data?.end_time || "—"}</p>
            </div>
          </div>

          {/* Section 4: System Order & Package Info */}
          <div className="border-b border-gray-700 pb-4">
            <h3 className="text-lg font-semibold text-secondary mb-3">
              {t("order.billing_system")}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              <p>
                <span className="text-gray-400">{t("order.order_status")}:</span>{" "}
                <span
                  className={`px-2 py-0.5 rounded text-xs font-semibold ${
                    data?.order_status === "CONFIRM"
                      ? "bg-green-600 text-white"
                      : data?.order_status === "PENDING"
                        ? "bg-yellow-600 text-white"
                        : data?.order_status === "REJECT"
                          ? "bg-red-500 text-white"
                          : "bg-gray-600 text-white"
                  }`}
                >
                  {data?.order_status}
                </span>
              </p>
              <p>
                <span className="text-gray-400">{t("order.system_status")}:</span>{" "}
                <span className="capitalize bg-purple-900/40 text-purple-300 px-2 py-0.5 rounded text-xs">
                  {data?.status}
                </span>
              </p>
              <p><span className="text-gray-400">{t("order.package")}:</span> {data?.package_title}</p>
              <p><span className="text-gray-400">{t("order.package_price")}:</span> {data?.price}</p>
              <p><span className="text-gray-400">{t("order.payment_method")}:</span> {data?.payment_method || t("order.not_specified")}</p>
              <p><span className="text-gray-400">{t("order.created_at")}:</span> {data?.created_at}</p>
            </div>
          </div>

          {/* Section 5: Publisher / User Details */}
          <div>
            <h3 className="text-lg font-semibold text-secondary mb-3">
              {t("order.user_information")}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              <p><span className="text-gray-400">{t("order.user_id")}:</span> #{data?.user_id}</p>
              <p><span className="text-gray-400">{t("order.username")}:</span> {data?.user_name}</p>
              <p><span className="text-gray-400">{t("order.full_name")}:</span> {data?.full_name}</p>
              <p><span className="text-gray-400">{t("order.email")}:</span> {data?.user_email}</p>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
});

export default OrderDetailsPop;